import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import MotionSection from '../components/ui/MotionSection';
import PaywallModal from '../components/ui/PaywallModal';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShuffle, FiTrash2, FiCopy, FiSave, FiDownload, FiEdit3, FiMoreHorizontal } = FiIcons;

const App = () => {
  const { user, plan } = useAuth();
  const { usage, incrementUsage, saveEmail } = useData();
  const [showPaywall, setShowPaywall] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedEmails, setGeneratedEmails] = useState([]);

  const [formData, setFormData] = useState({
    offer: '',
    persona: 'Founder',
    painPoint: '',
    valueProposition: '',
    cta: 'Book a call',
    tone: 'Friendly',
    framework: 'AIDA',
    length: 'Medium',
    variables: '{"first_name": "John", "company": "Acme Corp"}'
  });

  const personas = ['Founder', 'Marketing Lead', 'SMB Owner', 'Recruiter', 'Other'];
  const ctas = ['Book a call', 'Free trial', 'Quick reply', 'Demo'];
  const tones = ['Friendly', 'Formal', 'Bold', 'Conversational', 'Concise'];
  const frameworks = ['AIDA', 'PAS', 'BAB'];
  const lengths = ['Short', 'Medium', 'Long'];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateEmails = async () => {
    // Check usage limits
    if (plan === 'free' && usage.today >= 10) {
      setShowPaywall(true);
      return;
    }

    setIsGenerating(true);
    
    // Simulate AI generation
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const sampleEmails = [
      {
        id: 1,
        subject: `Quick question about ${formData.variables ? JSON.parse(formData.variables).company : '[Company]'}'s growth`,
        body: `Hi ${formData.variables ? JSON.parse(formData.variables).first_name : '{first_name}'},\n\nI noticed ${formData.variables ? JSON.parse(formData.variables).company : '{company}'} has been expanding rapidly. \n\n${formData.offer} has helped similar companies increase their ${formData.painPoint} by 40%.\n\nWould you be open to a 15-minute call to discuss how this could work for your team?\n\nBest regards,\n[Your Name]`,
        notes: 'Direct approach with social proof'
      },
      {
        id: 2,
        subject: `${formData.valueProposition} for ${formData.variables ? JSON.parse(formData.variables).company : '[Company]'}`,
        body: `Hi ${formData.variables ? JSON.parse(formData.variables).first_name : '{first_name}'},\n\nMost ${formData.persona.toLowerCase()}s struggle with ${formData.painPoint}.\n\nThat's exactly why we built ${formData.offer}.\n\n${formData.valueProposition}\n\nInterested in seeing how this works? I can show you in 10 minutes.\n\nCheers,\n[Your Name]`,
        notes: 'Problem-agitation-solution approach'
      },
      {
        id: 3,
        subject: `[Company] + [Your Product] = 30% more efficiency?`,
        body: `${formData.variables ? JSON.parse(formData.variables).first_name : '{first_name}'},\n\nBefore: ${formData.painPoint}\nAfter: ${formData.valueProposition}\n\nThe bridge? ${formData.offer}.\n\nWant to see the transformation in action?\n\nBest,\n[Your Name]`,
        notes: 'Before-after-bridge framework'
      }
    ];

    setGeneratedEmails(sampleEmails);
    incrementUsage();
    setIsGenerating(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  const saveEmailToProject = (email) => {
    if (!user) return;
    
    saveEmail({
      subject: email.subject,
      body: email.body,
      persona: formData.persona,
      tone: formData.tone,
      framework: formData.framework,
      length: formData.length,
      variables: formData.variables
    });
  };

  const createVariant = (email) => {
    if (plan === 'free') {
      setShowPaywall(true);
      return;
    }
    
    // Create A/B variant logic here
    const variant = {
      ...email,
      id: Date.now(),
      subject: email.subject + ' (Variant)',
      body: email.body.replace('Quick question', 'Urgent question'),
      notes: 'A/B test variant'
    };
    
    setGeneratedEmails(prev => [...prev, variant]);
  };

  const randomizeInputs = () => {
    const sampleOffers = [
      'AI-powered CRM software',
      'Marketing automation platform',
      'Lead generation tool',
      'Sales analytics dashboard'
    ];
    
    const samplePainPoints = [
      'low conversion rates',
      'manual data entry',
      'poor lead quality',
      'lack of sales visibility'
    ];
    
    const sampleValueProps = [
      'Increase conversions by 40%',
      'Save 10 hours per week',
      'Generate 3x more qualified leads',
      'Get real-time sales insights'
    ];

    setFormData(prev => ({
      ...prev,
      offer: sampleOffers[Math.floor(Math.random() * sampleOffers.length)],
      painPoint: samplePainPoints[Math.floor(Math.random() * samplePainPoints.length)],
      valueProposition: sampleValueProps[Math.floor(Math.random() * sampleValueProps.length)],
      persona: personas[Math.floor(Math.random() * personas.length)],
      tone: tones[Math.floor(Math.random() * tones.length)],
      framework: frameworks[Math.floor(Math.random() * frameworks.length)]
    }));
  };

  const clearForm = () => {
    setFormData({
      offer: '',
      persona: 'Founder',
      painPoint: '',
      valueProposition: '',
      cta: 'Book a call',
      tone: 'Friendly',
      framework: 'AIDA',
      length: 'Medium',
      variables: '{"first_name": "John", "company": "Acme Corp"}'
    });
    setGeneratedEmails([]);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Usage indicator */}
        {plan === 'free' && (
          <div className="mb-6 p-4 bg-primary/10 border border-primary/20 rounded-lg">
            <p className="text-sm">
              Free plan: {usage.today}/10 generations today
              {usage.today >= 8 && (
                <span className="ml-2 text-primary">
                  <a href="/pricing" className="underline">Upgrade for unlimited</a>
                </span>
              )}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Prompt Bar */}
          <div className="lg:col-span-1">
            <MotionSection className="sticky top-24">
              <div className="gradient-border rounded-xl p-6">
                <div className="gradient-border-inner p-6">
                  <h2 className="text-2xl font-bold mb-6">Email Generator</h2>
                  
                  <div className="space-y-6">
                    {/* Offer */}
                    <div>
                      <label className="block text-sm font-medium mb-2">Your Offer / Product</label>
                      <textarea
                        value={formData.offer}
                        onChange={(e) => handleInputChange('offer', e.target.value)}
                        placeholder="Describe your product or service..."
                        className="w-full px-4 py-3 bg-dark border border-primary/20 rounded-lg focus:outline-none focus:border-primary resize-none h-20"
                      />
                    </div>

                    {/* Target Persona */}
                    <div>
                      <label className="block text-sm font-medium mb-2">Target Persona</label>
                      <select
                        value={formData.persona}
                        onChange={(e) => handleInputChange('persona', e.target.value)}
                        className="w-full px-4 py-3 bg-dark border border-primary/20 rounded-lg focus:outline-none focus:border-primary"
                      >
                        {personas.map(persona => (
                          <option key={persona} value={persona}>{persona}</option>
                        ))}
                      </select>
                    </div>

                    {/* Pain Point */}
                    <div>
                      <label className="block text-sm font-medium mb-2">Pain Point</label>
                      <input
                        type="text"
                        value={formData.painPoint}
                        onChange={(e) => handleInputChange('painPoint', e.target.value)}
                        placeholder="What problem do you solve?"
                        className="w-full px-4 py-3 bg-dark border border-primary/20 rounded-lg focus:outline-none focus:border-primary"
                      />
                    </div>

                    {/* Value Proposition */}
                    <div>
                      <label className="block text-sm font-medium mb-2">Value Proposition</label>
                      <input
                        type="text"
                        value={formData.valueProposition}
                        onChange={(e) => handleInputChange('valueProposition', e.target.value)}
                        placeholder="What's the key benefit?"
                        className="w-full px-4 py-3 bg-dark border border-primary/20 rounded-lg focus:outline-none focus:border-primary"
                      />
                    </div>

                    {/* CTA */}
                    <div>
                      <label className="block text-sm font-medium mb-2">Call-to-Action</label>
                      <select
                        value={formData.cta}
                        onChange={(e) => handleInputChange('cta', e.target.value)}
                        className="w-full px-4 py-3 bg-dark border border-primary/20 rounded-lg focus:outline-none focus:border-primary"
                      >
                        {ctas.map(cta => (
                          <option key={cta} value={cta}>{cta}</option>
                        ))}
                      </select>
                    </div>

                    {/* Tone */}
                    <div>
                      <label className="block text-sm font-medium mb-2">Tone</label>
                      <div className="flex flex-wrap gap-2">
                        {tones.map(tone => (
                          <button
                            key={tone}
                            onClick={() => handleInputChange('tone', tone)}
                            className={`px-3 py-1 rounded-full text-sm transition-colors ${
                              formData.tone === tone
                                ? 'bg-primary text-white'
                                : 'bg-dark border border-primary/20 hover:border-primary'
                            }`}
                          >
                            {tone}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Framework */}
                    <div>
                      <label className="block text-sm font-medium mb-2">Framework</label>
                      <div className="flex gap-2">
                        {frameworks.map(framework => (
                          <button
                            key={framework}
                            onClick={() => handleInputChange('framework', framework)}
                            className={`px-3 py-1 rounded-full text-sm transition-colors ${
                              formData.framework === framework
                                ? 'bg-primary text-white'
                                : 'bg-dark border border-primary/20 hover:border-primary'
                            }`}
                          >
                            {framework}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Length */}
                    <div>
                      <label className="block text-sm font-medium mb-2">Email Length</label>
                      <div className="flex gap-2">
                        {lengths.map(length => (
                          <button
                            key={length}
                            onClick={() => handleInputChange('length', length)}
                            className={`px-3 py-1 rounded-full text-sm transition-colors ${
                              formData.length === length
                                ? 'bg-primary text-white'
                                : 'bg-dark border border-primary/20 hover:border-primary'
                            }`}
                          >
                            {length}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Variables */}
                    <div>
                      <label className="block text-sm font-medium mb-2">Variables (JSON)</label>
                      <textarea
                        value={formData.variables}
                        onChange={(e) => handleInputChange('variables', e.target.value)}
                        placeholder='{"first_name": "John", "company": "Acme"}'
                        className="w-full px-4 py-3 bg-dark border border-primary/20 rounded-lg focus:outline-none focus:border-primary resize-none h-16 text-sm font-mono"
                      />
                    </div>

                    {/* Buttons */}
                    <div className="space-y-3">
                      <button
                        onClick={generateEmails}
                        disabled={isGenerating || !formData.offer.trim()}
                        className="w-full btn-primary py-3 rounded-lg text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isGenerating ? 'Generating...' : 'Generate Emails'}
                      </button>
                      
                      <div className="flex gap-2">
                        <button
                          onClick={randomizeInputs}
                          className="flex-1 flex items-center justify-center space-x-2 py-2 border border-primary/20 rounded-lg hover:border-primary transition-colors"
                        >
                          <SafeIcon icon={FiShuffle} className="w-4 h-4" />
                          <span>Randomize</span>
                        </button>
                        <button
                          onClick={clearForm}
                          className="flex-1 flex items-center justify-center space-x-2 py-2 border border-primary/20 rounded-lg hover:border-primary transition-colors"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          <span>Clear</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </MotionSection>
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-2">
            {generatedEmails.length === 0 ? (
              <MotionSection className="h-96 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-xl text-light/60 mb-4">
                    Describe your offer and audience, then hit Generate.
                  </p>
                  <p className="text-light/40">
                    Your AI-generated emails will appear here.
                  </p>
                </div>
              </MotionSection>
            ) : (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold">Generated Emails</h3>
                  {plan === 'pro' && (
                    <button className="flex items-center space-x-2 px-4 py-2 border border-primary/20 rounded-lg hover:border-primary transition-colors">
                      <SafeIcon icon={FiDownload} className="w-4 h-4" />
                      <span>Export All</span>
                    </button>
                  )}
                </div>

                <motion.div 
                  className="space-y-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ staggerChildren: 0.1 }}
                >
                  {generatedEmails.map((email, index) => (
                    <motion.div
                      key={email.id}
                      initial={{ opacity: 0, y: 30, filter: 'blur(6px)' }}
                      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="gradient-border rounded-xl p-6"
                    >
                      <div className="gradient-border-inner p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <h4 className="font-semibold text-lg mb-2">Subject: {email.subject}</h4>
                            <p className="text-sm text-primary/80 mb-4">{email.notes}</p>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => copyToClipboard(`Subject: ${email.subject}\n\n${email.body}`)}
                              className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
                              title="Copy email"
                            >
                              <SafeIcon icon={FiCopy} className="w-4 h-4" />
                            </button>
                            {user && (
                              <button
                                onClick={() => saveEmailToProject(email)}
                                className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
                                title="Save to project"
                              >
                                <SafeIcon icon={FiSave} className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => createVariant(email)}
                              className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
                              title="Create A/B variant"
                            >
                              <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="bg-dark/50 p-4 rounded-lg">
                          <pre className="whitespace-pre-wrap text-sm leading-relaxed">
                            {email.body}
                          </pre>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            )}
          </div>
        </div>
      </div>

      <PaywallModal 
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
      />
    </motion.div>
  );
};

export default App;